import React, { Suspense, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Search } from '@mui/icons-material';

function Layout(props) {
  const { t, i18n } = useTranslation(['translation']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchVal, setSearchVal] = useState('');

  // initialize from URL query
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q') || '';
    setSearchVal(q);
  }, [location.search]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // debounce navigation to avoid frequent route updates while typing
  const searchDebounceRef = React.useRef();
  const handleSearch = (e) => {
    e.preventDefault();
  };

  // cleanup debounce on unmount
  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return (
    <Suspense fallback={<div className="my-spinner">Loading...</div>}>
      <header className="wecobook-header" style={{ position: 'sticky', top: 0, zIndex: 1000, padding: '12px 0' }}>
        <Container maxWidth="xl">
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            {/* Logo */}
            <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
              <div className="wecobook-brand" onClick={() => navigate('/')}>
                <span className="brand-logo-w">W</span>
                <span>WecoBook</span>
              </div>
            </Grid>

            {/* Search Bar */}
            <Grid item xs={12} md={5} style={{ display: 'flex', justifyContent: 'center' }}>
              <form onSubmit={handleSearch} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="search-container">
                  <Search style={{ color: '#64748b', fontSize: 20 }} />
                  <input
                    type="text"
                    placeholder="Search countries or indicators..."
                    value={searchVal}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSearchVal(v);
                      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                      searchDebounceRef.current = setTimeout(() => {
                        const q = v ? `?q=${encodeURIComponent(v)}` : '';
                        navigate(`/${q}`, { replace: true });
                      }, 350);
                    }}
                    aria-label="Search site"
                  />
                </div>
              </form>
            </Grid>

            {/* Nav & Language Switch */}
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
              <div className="nav-tabs-container">
                <button
                  className={`nav-tab-btn ${location.pathname === '/' ? 'active' : ''}`}
                  onClick={() => navigate('/')}
                >
                  Catalog
                </button>
                <button
                  className={`nav-tab-btn ${location.pathname === '/map' ? 'active' : ''}`}
                  onClick={() => navigate('/map')}
                >
                  Map
                </button>
                <button
                  className={`nav-tab-btn ${location.pathname === '/compare' ? 'active' : ''}`}
                  onClick={() => navigate('/compare')}
                >
                  Compare
                </button>
              </div>

              <div style={{ display: 'flex', gap: '6px', borderLeft: '1px solid #cbd5e1', paddingLeft: '16px' }}>
                <button
                  onClick={() => changeLanguage('ua')}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: i18n.language === 'ua' ? 'bold' : 'normal',
                    color: i18n.language === 'ua' ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}
                >
                  UA
                </button>
                <span style={{ color: '#cbd5e1', fontSize: '13px' }}>|</span>
                <button
                  onClick={() => changeLanguage('en')}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: i18n.language === 'en' ? 'bold' : 'normal',
                    color: i18n.language === 'en' ? 'var(--text-primary)' : 'var(--text-secondary)'
                  }}
                >
                  EN
                </button>
              </div>
            </Grid>
          </Grid>
        </Container>
      </header>

      {/* Main Body */}
      <main style={{ minHeight: 'calc(100vh - 180px)', padding: '24px 0' }}>
        <Container maxWidth="xl">
          {props.children}
        </Container>
      </main>

      {/* Footer */}
      <footer className="wecobook-footer">
        <Container maxWidth="xl">
          <p>© {new Date().getFullYear()} WecoBook. All rights reserved. Privacy Policy. Terms of Service.</p>
        </Container>
      </footer>
    </Suspense>
  );
}

export default Layout;